"use client";

import {
  createContext,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";

/** Mutable UI state. Changes often (page loading ticks, modal toggles). */
interface UIStateValue {
  sidebarOpen: boolean;
  authModalOpen: boolean;
  proModalOpen: boolean;
  /** True while a route Suspense fallback or screen data load is in flight. */
  isPageLoading: boolean;
}

/**
 * Imperative handles. Deliberately kept in a separate context with a value
 * that never changes identity, so components that only dispatch (tool cards,
 * `useReportPageLoading`) never re-render when UI state moves.
 */
interface UIActionsValue {
  openSidebar: () => void;
  closeSidebar: () => void;
  openAuthModal: () => void;
  closeAuthModal: () => void;
  openProModal: () => void;
  closeProModal: () => void;
  beginPageLoading: () => void;
  endPageLoading: () => void;
}

type UIContextValue = UIStateValue & UIActionsValue;

const UIStateContext = createContext<UIStateValue | null>(null);
const UIActionsContext = createContext<UIActionsValue | null>(null);

export function UIProvider({ children }: { children: ReactNode }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [proModalOpen, setProModalOpen] = useState(false);
  const [pageLoadingCount, setPageLoadingCount] = useState(0);

  const actions = useMemo<UIActionsValue>(
    () => ({
      openSidebar: () => setSidebarOpen(true),
      closeSidebar: () => setSidebarOpen(false),
      openAuthModal: () => setAuthModalOpen(true),
      closeAuthModal: () => setAuthModalOpen(false),
      openProModal: () => setProModalOpen(true),
      closeProModal: () => setProModalOpen(false),
      beginPageLoading: () => setPageLoadingCount((count) => count + 1),
      endPageLoading: () => setPageLoadingCount((count) => Math.max(0, count - 1)),
    }),
    [],
  );

  const state = useMemo<UIStateValue>(
    () => ({
      sidebarOpen,
      authModalOpen,
      proModalOpen,
      isPageLoading: pageLoadingCount > 0,
    }),
    [sidebarOpen, authModalOpen, proModalOpen, pageLoadingCount],
  );

  return (
    <UIActionsContext.Provider value={actions}>
      <UIStateContext.Provider value={state}>{children}</UIStateContext.Provider>
    </UIActionsContext.Provider>
  );
}

/** Actions only — subscribing here never causes a re-render. */
export function useUIActions(): UIActionsValue {
  const ctx = useContext(UIActionsContext);
  if (!ctx) throw new Error("useUIActions must be used within <UIProvider>");
  return ctx;
}

/** State only — re-renders when any UI flag changes. */
export function useUIState(): UIStateValue {
  const ctx = useContext(UIStateContext);
  if (!ctx) throw new Error("useUIState must be used within <UIProvider>");
  return ctx;
}

/**
 * State + actions. Prefer `useUIActions` when a component only dispatches:
 * `useUI` subscribes to state as well and re-renders with it.
 */
export function useUI(): UIContextValue {
  const state = useUIState();
  const actions = useUIActions();
  return useMemo(() => ({ ...state, ...actions }), [state, actions]);
}
